/**
 * RisksListPage - View and manage all reported risks
 */
import React, { useState, useEffect } from 'react';
import api, { formatDate } from '../lib/api';
import { STATUS_CONFIG, SEVERITY_CONFIG, REPORT_TYPES } from '../lib/constants';
import { Card, StatusBadge, SeverityBadge, TypeBadge, Spinner, EmptyState, Select, CustomButton as Button } from '../components/shared';

const RisksListPage = ({ setCurrentPage, setSelectedRisk, currentRole }) => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', site_id: '', report_type: '', severity: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [sites, setSites] = useState([]);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [risksRes, sitesRes] = await Promise.all([
        api.get(`/risks?${new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v]) => v)))}`),
        api.get('/sites')
      ]);
      setRisks(risksRes.items);
      setSites(sitesRes.items);
    } catch (err) {
      console.error('Failed to load risks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
  };

  const clearAllFilters = () => {
    setFilters({ status: '', site_id: '', report_type: '', severity: '' });
    setSearchQuery('');
  };

  // Get active filters for chips display
  const activeFilters = Object.entries(filters)
    .filter(([, v]) => v)
    .map(([key, value]) => {
      let label = '';
      let displayValue = value;
      
      switch (key) {
        case 'status':
          label = 'Status';
          displayValue = STATUS_CONFIG[value]?.label || value;
          break;
        case 'site_id':
          label = 'Site';
          displayValue = sites.find(s => s.id === value)?.name || value;
          break;
        case 'report_type':
          label = 'Type';
          displayValue = REPORT_TYPES.find(t => t.id === value)?.label || value;
          break;
        case 'severity':
          label = 'Severity';
          displayValue = SEVERITY_CONFIG[value]?.label || value;
          break;
        default:
          label = key;
      }
      
      return { key, label, displayValue };
    });

  // Filter risks by search query
  const filteredRisks = risks.filter(risk => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      risk.title?.toLowerCase().includes(query) ||
      risk.id?.toLowerCase().includes(query) ||
      risk.risk_owner_name?.toLowerCase().includes(query) ||
      risk.site_name?.toLowerCase().includes(query)
    );
  });

  const hasActiveFilters = activeFilters.length > 0 || searchQuery;

  return (
    <div className="page risks-page" data-testid="risks-page">
      <div className="page-header">
        <div>
          <h1>Risk & Hazard Reports</h1>
          <p>View and manage all reported risks, hazards, and incidents</p>
        </div>
        <Button variant="primary" onClick={() => setCurrentPage('new-report')} data-testid="new-report-btn-page">
          + New Report
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="filters-card filters-sticky">
        {/* Search Box */}
        <div className="search-row">
          <div className="search-input-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="search-icon"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by title, ID, owner, or site..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>×</button>
            )}
          </div>
        </div>
        
        {/* Filter Dropdowns */}
        <div className="filters-row">
          <Select
            label="Status"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            options={[
              { value: '', label: 'All Statuses' },
              ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))
            ]}
          />
          <Select
            label="Site"
            value={filters.site_id}
            onChange={(e) => handleFilterChange('site_id', e.target.value)}
            options={[
              { value: '', label: 'All Sites' },
              ...sites.map(s => ({ value: s.id, label: s.name }))
            ]}
          />
          <Select
            label="Type"
            value={filters.report_type}
            onChange={(e) => handleFilterChange('report_type', e.target.value)}
            options={[
              { value: '', label: 'All Types' },
              ...REPORT_TYPES.map(t => ({ value: t.id, label: t.label }))
            ]}
          />
          <Select
            label="Severity"
            value={filters.severity}
            onChange={(e) => handleFilterChange('severity', e.target.value)}
            options={[
              { value: '', label: 'All Severities' },
              ...Object.entries(SEVERITY_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))
            ]}
          />
        </div>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <div className="filter-chips" data-testid="filter-chips">
            {searchQuery && (
              <span className="filter-chip">
                <span className="filter-chip-label">Search:</span>
                <span className="filter-chip-value">&ldquo;{searchQuery}&rdquo;</span>
                <button className="filter-chip-clear" onClick={() => setSearchQuery('')}>×</button>
              </span>
            )}
            {activeFilters.map(({ key, label, displayValue }) => (
              <span key={key} className="filter-chip">
                <span className="filter-chip-label">{label}:</span>
                <span className="filter-chip-value">{displayValue}</span>
                <button className="filter-chip-clear" onClick={() => clearFilter(key)}>×</button>
              </span>
            ))}
            <button className="filter-clear-all" onClick={clearAllFilters}>
              Clear all
            </button>
          </div>
        )}
      </Card>

      {/* Risks Table */}
      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : filteredRisks.length === 0 ? (
        <EmptyState 
          icon="📋" 
          title="No reports found" 
          description={hasActiveFilters ? "No reports match the current filters" : "No reports have been created yet"}
          action={<Button variant="primary" onClick={() => setCurrentPage('new-report')}>Create First Report</Button>}
        />
      ) : (
        <Card className="table-card">
          <table className="data-table" data-testid="risks-table">
            <thead>
              <tr>
                <th className="col-id">ID</th>
                <th>Type</th>
                <th>Title</th>
                <th>Site</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Owner</th>
                <th className="col-date">Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredRisks.map(risk => (
                <tr 
                  key={risk.id} 
                  onClick={() => { setSelectedRisk(risk.id); setCurrentPage('risk-detail'); }}
                  className={`table-row ${risk.restricted ? 'restricted' : ''}`}
                  data-testid={`risk-row-${risk.id}`}
                >
                  <td className="cell-id"><code>{risk.id.slice(0, 8)}</code></td>
                  <td><TypeBadge type={risk.report_type} /></td>
                  <td className="cell-title">
                    {risk.restricted && <span className="restricted-icon" title="Restricted">🔒</span>}
                    {risk.immediate_danger && <span className="danger-icon" title="Immediate Danger">🚨</span>}
                    {risk.title}
                  </td>
                  <td>{risk.site_name}</td>
                  <td><SeverityBadge severity={risk.severity} /></td>
                  <td><StatusBadge status={risk.status} /></td>
                  <td>{risk.risk_owner_name || '-'}</td>
                  <td className="cell-date">{formatDate(risk.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default RisksListPage;
